// @givmo/social — every word the Social pages say (SMM-WEB1). One file, so a review reads the
// module's voice in one place and a test can pin a sentence without hunting for it.
//
// The voice: plain, short, and honest about what happened. A post that may have gone out says so
// and says what to check; a post that did not go out says why in the organization's terms, never
// in a platform's error body (the backend stores a closed code, never the platform's words).

import { platformLabel } from './social-platforms';

export const socialWords = {
  moduleName: 'Social',

  // ── Pages ───────────────────────────────────────────────────────────────────────────────────
  postsTitle: 'Posts',
  postsLede: 'What your organization has posted, and what’s scheduled.',
  newPost: 'New post',
  composeTitle: 'New post',
  editDraftTitle: 'Edit draft',
  postTitle: 'Post',
  accountsTitle: 'Accounts',
  accountsLede: 'The pages and accounts your organization posts to.',
  backToPosts: 'Posts',

  // ── Access ──────────────────────────────────────────────────────────────────────────────────
  noAccessTitle: 'You don’t have access to Social',
  noAccessBody: 'Ask an admin of your organization for access to Social.',
  // A renter's composer, for someone who may read Social's posts but not write them.
  noPostAccessTitle: 'You can’t post for your organization',
  noPostAccessBody: 'Ask an admin of your organization for access to post on Social.',
  close: 'Close',
  noOrgTitle: 'Your organization isn’t available',
  noOrgBody: 'Once your organization is available, its posts appear here.',
  loadingPosts: 'Loading posts…',
  loadingPost: 'Loading the post…',
  loadingAccounts: 'Loading accounts…',
  readFailedTitle: 'Couldn’t load this',
  readFailedBody: 'Something went wrong on our side. Please try again.',
  postGoneTitle: 'This post isn’t available',
  postGoneBody: 'It may have been deleted.',
  tryAgain: 'Try again',

  // ── Connected accounts ─────────────────────────────────────────────────────────────────────
  connect: 'Connect',
  connectedHeading: 'Connected',
  addHeading: 'Add an account',
  familyLine: (family: string) =>
    family === 'meta'
      ? 'Facebook Pages, and the Instagram accounts linked to them'
      : family === 'linkedin'
        ? 'LinkedIn Pages you’re an admin of'
        : '',
  connectFamily: (family: string) => `Connect ${family}`,
  reconnect: 'Reconnect',
  disconnect: 'Disconnect',
  rowActions: (name: string) => `Actions for ${name}`,
  noAccountsTitle: 'No accounts connected',
  noAccountsManager:
    'Connect a Facebook Page, an Instagram account or a LinkedIn page to start posting.',
  noAccountsMember: 'An admin of your organization connects the accounts Social posts to.',
  noneOffered: 'Connecting accounts isn’t available here yet.',
  // A renter's composer before any account is live (frame 39).
  connectStepTitle: (org: string | null) =>
    org ? `Connect ${org}’s social accounts` : 'Connect your social accounts',
  connectStepLine: (org: string | null) =>
    org
      ? `Connect once. Every ${org} campaign can post to them.`
      : 'Connect once. Every campaign can post to them.',
  notConnected: 'Not connected',
  whatGivmoDoes:
    'Givmo publishes only the posts your organization writes and schedules here, to the Pages and accounts you choose. It never posts on its own.',
  instagramHint:
    'Don’t see your Instagram? It has to be a business or creator account linked to your Facebook Page.',
  needsReconnect: 'Needs reconnecting',
  reconnectBy: (date: string) => `Reconnect by ${date}`,
  connectedOn: (date: string) => `Connected ${date}`,
  connectedBy: (name: string) => `by ${name}`,
  unnamedAccount: 'Unnamed account',
  connectingTo: (family: string) => `Taking you to ${family}…`,
  startFailed: 'Couldn’t start connecting. Please try again.',

  // The disconnect confirmation (the privacy policy's own words for removing access, V7).
  disconnectTitle: (name: string, platform: string) => `Disconnect ${name} on ${platform}?`,
  scheduledToIt: (count: number, capped: boolean) =>
    count === 0
      ? 'Nothing is scheduled to it.'
      : `${capped ? 'More than ' : ''}${count.toLocaleString()} scheduled ${count === 1 && !capped ? 'post' : 'posts'} to it won’t go out.`,
  disconnectBody: (platform: string) =>
    `Givmo stops posting to it, and posts scheduled for it won’t go out. Posts already published stay on ${platform}.`,
  removeAtMeta:
    'To also remove Givmo’s access on Facebook, go to Settings, then Business integrations. That covers the Instagram accounts connected through your Pages.',
  removeAtLinkedInBefore: 'To also remove Givmo’s access on LinkedIn, use ',
  removeAtLinkedInAfter: ' in your LinkedIn settings.',
  linkedInPermittedServices: 'https://www.linkedin.com/psettings/permitted-services',
  permittedServices: 'Permitted services',
  disconnectConfirm: 'Disconnect',
  cancel: 'Cancel',
  disconnected: (name: string) => `${name} was disconnected`,
  disconnectFailed: 'Couldn’t disconnect the account. Please try again.',

  // ── The OAuth return page ──────────────────────────────────────────────────────────────────
  finishingTitle: 'Connecting your account…',
  connectedTitle: 'Connected',
  connectedOne: (name: string) => `${name} is connected.`,
  connectedMany: (count: number) => `${count} accounts are connected.`,
  reconnectedOne: (name: string) => `${name} is reconnected.`,
  chooseTitle: 'Choose the accounts to connect',
  // The store connects only what is ticked and leaves every other connection as it is; ticking
  // an account already connected mints it a fresh credential on its own row (a renewal).
  chooseBody: (org: string | null) =>
    org
      ? `Tick the Pages and accounts to connect to ${org}. Accounts already connected stay connected either way.`
      : 'Tick the Pages and accounts to connect. Accounts already connected stay connected either way.',
  chooseExpires: (minutes: number) =>
    minutes <= 1
      ? 'This choice expires in about a minute.'
      : `This choice expires in ${minutes} minutes.`,
  alreadyConnected: 'Already connected · ticking it renews the connection',
  reconnectsIt: 'Needs reconnecting · ticking it reconnects it',
  chooseConnect: 'Connect',
  chooseCancel: 'Cancel',
  chooseNone: 'Select at least one account.',
  choiceAbandoned: 'Nothing was connected.',
  done: 'Done',
  startAgain: 'Back to accounts',
  tryConnectAgain: 'Try again',
  notConnectedTitle: 'Nothing was connected',
  noCodeBody: 'This page finishes connecting an account. Start from Accounts.',

  // ── Composer ────────────────────────────────────────────────────────────────────────────────
  postTo: 'Post to',
  chooseAnAccount: 'Choose at least one account.',
  noChannels: 'Connect an account first.',
  needReconnecting: (name: string) => `${name} needs reconnecting before posts can go to it.`,
  askAdminToReconnect: 'Ask an admin of your organization to reconnect it.',
  goToAccounts: 'Accounts',
  connectAnAccount: 'Connect an account',
  askAdminToConnect: 'Ask an admin of your organization to connect its accounts.',
  textLabel: 'Text',
  textPlaceholder: 'What would you like to share?',
  imageLabel: 'Image',
  imageHint: 'JPEG, PNG or WebP, up to 8 MB.',
  addImage: 'Add image',
  removeImage: 'Remove',
  replaceImage: 'Replace',
  uploading: 'Uploading…',
  linkLabel: 'Link',
  linkPlaceholder: 'https://',
  optional: 'optional',
  campaignLabel: 'Campaign',
  campaignTagged: (label: string) => `Part of ${label}`,
  campaignUnnamed: 'this campaign',
  removeCampaign: 'Remove',
  fromCollateral: 'From Collateral',
  useGraphic: 'Use',
  suggestedTime: (time: string) => `Suggested time: ${time}`,
  previewTitle: 'How each account’s post will look',
  // A renter's card (frame 40): the same composer, its parts in the appeal tab's order.
  graphicLabel: 'Graphic',
  captionLabel: 'Caption',
  addAnImage: 'Add an image',
  removeTheImage: 'Remove the image',
  attachedImage: 'The post’s image',
  linksToCampaign: 'Links to the campaign:',
  linksTo: 'Links to:',
  instagramBio: (org: string | null) =>
    org ? `Add the link to ${org}’s Instagram bio` : 'Add the link to your Instagram bio',
  previewToggle: 'Preview',
  scheduleAtWords: (when: string, zone: string) => `${when} ${zone}`,
  previewEmpty: 'Choose an account to see its post.',
  customize: 'Edit for this account',
  useMainText: 'Use the main text',
  customized: 'Edited for this account',
  doneEditing: 'Done',
  editingHint: 'Only this account gets this text. Changes to the main text won’t reach it.',
  linkPreviewNote: 'Facebook shows this link as a preview.',
  linkCounts: 'including the link',
  untickTitle: (name: string, platform: string) => `Stop posting to ${name} on ${platform}?`,
  untickBody: 'The text you edited for this account will be lost.',
  untickConfirm: 'Stop posting to it',
  keepIt: 'Keep it',
  saveDraft: 'Save draft',
  saving: 'Saving…',
  draftSaved: 'Draft saved',
  publishNow: 'Publish now',
  schedule: 'Schedule',
  scheduleTitle: 'Schedule the post',
  scheduleDate: 'Date',
  scheduleTime: 'Time',
  scheduleConfirm: 'Schedule post',
  scheduleWindow: 'Between 2 minutes and 90 days from now.',
  scheduleInZone: (zone: string) => `In your time zone, ${zone}.`,
  scheduleReadBack: (when: string, zone: string, accounts: number) =>
    `${when} ${zone} · to ${accounts.toLocaleString()} ${accounts === 1 ? 'account' : 'accounts'}`,
  published: 'Your post is on its way',
  scheduled: (time: string) => `Scheduled for ${time}`,
  deleteDraft: 'Delete draft',
  deleteDraftTitle: 'Delete this draft?',
  deleteDraftBody: 'It hasn’t gone anywhere yet. Deleting it can’t be undone.',
  deleteConfirm: 'Delete',
  draftDeleted: 'Draft deleted',

  // Hints a card shows for its platform.
  hintInstagramImage: 'Instagram needs an image.',
  // Why Publish and Schedule wait (the first thing to fix, beside the buttons).
  blockNoText: 'Write something to post.',
  blockNoChannels: 'Choose at least one account.',
  blockBadLink: 'Fix the link to post.',
  blockTooManyChannels: 'Choose 20 accounts at most.',
  blockImage: (platform: string) => `Add an image, or untick ${platform}.`,
  blockImageShape: (platform: string) => `Use an image closer to square, or untick ${platform}.`,
  blockText: (platform: string) => `Shorten ${platform}’s post, or untick ${platform}.`,
  hintInstagramLink: 'Links in Instagram captions aren’t clickable.',

  // A card's problem (the backend's `variant_text_problem` and its image rules).
  problemTextRequired: 'Write something to post.',
  problemTooLong: (over: number, platform: string, linkCounted: boolean) =>
    `${over.toLocaleString()} ${over === 1 ? 'character' : 'characters'} over what ${platform} takes${linkCounted ? ' with the link' : ''}.`,
  problemHashtags: 'Instagram takes at most 30 hashtags.',
  problemMentions: 'Instagram takes at most 20 @ mentions.',
  problemImageRequired: 'Add an image to post to Instagram.',
  problemImageShape:
    'Instagram takes images from 4:5 (portrait) to 1.91:1 (landscape). Choose another image.',

  // Field problems.
  linkNotHttps: 'Use a link that starts with https://',
  linkTooLong: 'That link is too long.',
  baseTooLong: 'The text is longer than 5,000 characters.',
  scheduleTooSoon: 'Choose a time at least 2 minutes from now.',
  scheduleTooFar: 'Choose a time within the next 90 days.',
  scheduleInvalid: 'Choose a date and a time.',
  imageTooBig: 'That image is larger than 8 MB.',
  imageWrongType: 'Use a JPEG, PNG or WebP image.',

  // ── History ─────────────────────────────────────────────────────────────────────────────────
  filterAll: 'All',
  filterDrafts: 'Drafts',
  filterScheduled: 'Scheduled',
  filterPublished: 'Published',
  filterFailed: 'Needs attention',
  attentionLine: (count: number, capped: boolean) =>
    `${capped ? 'More than ' : ''}${count.toLocaleString()} ${count === 1 && !capped ? 'post needs' : 'posts need'} attention.`,
  showThem: 'Show them',
  filterLabel: 'Show',
  campaignFilter: (label: string) => `Posts for ${label}`,
  clearCampaignFilter: 'Show all posts',
  noPostsTitle: 'No posts yet',
  noPostsBody: 'Posts your organization writes and schedules appear here.',
  noFilteredPosts: 'No posts match.',
  draftBy: (name: string) => `Draft by ${name}`,
  by: (name: string) => `by ${name}`,
  edit: 'Edit',
  view: 'View',
  viewPost: 'View post',
  retry: 'Retry',
  retried: 'Posting again',
  cancelScheduled: 'Unschedule',
  cancelled: 'Back to a draft',
  cancelledSome: 'Unscheduled what hadn’t gone out yet',
  retryUnknownTitle: 'Retry this post?',
  retryUnknownBody: (account: string, platform: string) =>
    `It may already be on ${account}. Check ${platform} first: if it’s there, retrying posts it twice.`,
  retryAnyway: 'Retry',
  retryLateTitle: 'Post it now?',
  retryLateBody: (when: string) =>
    `This was scheduled for ${when}. Check the text is still right before you post it.`,
  postNow: 'Post now',
  checkAccount: (platform: string) => `Check ${platform}`,
  editScheduled: 'Edit',
  editScheduledTitle: 'Edit this scheduled post?',
  editScheduledBody:
    'It goes back to a draft and won’t go out until you schedule or publish it again.',
  editScheduledConfirm: 'Edit post',
  scheduleOutOfWindow: 'Choose a time between 2 minutes and 90 days from now.',
  channelUnavailable: 'One of the accounts can’t be posted to right now. Check Accounts.',
  postAlreadySent: 'Part of this post has already gone out, so it can’t be changed.',
  nothingToCancel: 'Nothing in this post is still scheduled.',
  notRetryable: 'This post can’t be retried.',
  imageUnreadable: 'We couldn’t read that image. Try another.',
  mediaUnavailable: 'Images can’t be added right now. Please try again later.',
  graphicNotAnImage: 'Only a PNG or JPEG graphic can go in a post.',
  graphicOutOfDate: 'That graphic is marked out of date. Put it back in use in Collateral first.',
  actionFailed: 'Something went wrong. Please try again.',
  previous: 'Previous',
  next: 'Next',
  page: (from: number, to: number, total: number) => `${from}–${to} of ${total}`,

  // A variant's state, as a plain word.
  statusDraft: 'Draft',
  statusQueued: 'Scheduled',
  statusPublishing: 'Posting',
  statusPublished: 'Published',
  statusFailed: 'Didn’t post',
  statusUnknown: 'Couldn’t confirm it published',
  statusRemoved: 'Removed',
  at: (time: string) => time,
  publishedAt: (time: string) => `Published ${time}`,
  scheduledFor: (time: string) => `Scheduled for ${time}`,
  wasScheduledFor: (time: string) => `Was scheduled for ${time}`,
  deferredTo: (time: string) => `Waiting until ${time}`,
  deferredWhy: 'Givmo sends at most 25 posts a day per organization to each platform.',
  accountGone: 'This account was disconnected.',
} as const;

/**
 * Why a variant didn't post, from its closed `failure_reason` (backend `SocialFailureReason`).
 * `title` is the line on the row; `detail` says what it means and what to do. A code this build
 * doesn't know reads as the platform's refusal, never as nothing.
 */
export function failureWords(
  reason: string | null | undefined,
  platform: string,
): { title: string; detail: string } {
  const name = platformLabel(platform);
  switch (reason) {
    case 'outcome_unknown':
      return {
        title: socialWords.statusUnknown,
        detail: `${name} didn’t answer after we sent it, so it may have posted. Check the account before you retry, so it doesn’t post twice.`,
      };
    case 'too_late':
      return {
        title: 'Too late to post at its time',
        detail: 'Givmo couldn’t send it within 3 hours of its scheduled time, so it wasn’t posted.',
      };
    case 'daily_limit_too_late':
      return {
        title: 'Daily limit reached',
        detail: `Givmo sends at most 25 posts a day per organization to each platform, and the next opening on ${name} was more than 3 hours after its time.`,
      };
    case 'content_rejected':
      return {
        title: `${name} didn’t accept the words`,
        detail: 'Start a new post with different words.',
      };
    case 'media_rejected':
      return {
        title: `${name} didn’t accept the image`,
        detail: 'Retry, or start a new post with a different image.',
      };
    case 'reconnect_required':
      return {
        title: 'The account needs reconnecting',
        detail: 'Its access has ended. An admin can reconnect it under Accounts.',
      };
    case 'account_disconnected':
      return { title: 'The account was disconnected', detail: 'Connect it again to post to it.' };
    case 'platform_unavailable':
      return {
        title: `Posting to ${name} isn’t available`,
        detail: 'It’s switched off here for now.',
      };
    case 'module_unavailable':
      return {
        title: 'Social was switched off',
        detail: 'Social isn’t on for your organization.',
      };
    case 'author_not_permitted':
      return {
        title: 'Its author can no longer post',
        detail:
          'The person who wrote it no longer has access to post, so it can’t go out as theirs. Someone with access can start a new post with the same words.',
      };
    case 'author_protected':
      return {
        title: 'Its author can’t post publicly',
        detail:
          'It can’t go out as theirs. Someone else in your organization can start a new post with the same words.',
      };
    case 'organization_ineligible':
      return {
        title: 'Your organization can’t post right now',
        detail: 'Your organization’s Givmo account needs attention before it can post.',
      };
    case 'company_guard_pending':
      return {
        title: 'Posting isn’t available for your organization yet',
        detail: 'Nothing was sent.',
      };
    case 'organization_closed':
      return {
        title: 'The organization’s account was closed',
        detail: 'Nothing more is posted from a closed account.',
      };
    case 'image_unavailable':
      return {
        title: 'The image is no longer available',
        detail: 'Start a new post and add the image again.',
      };
    case 'container_failed':
      return {
        title: 'Instagram couldn’t prepare the image',
        detail: 'Retry, or start a new post with a different image.',
      };
    default:
      return {
        title: `${name} didn’t take the post`,
        detail: 'Try again. If it keeps happening, check the account on the platform.',
      };
  }
}

/** The connect's refusals, keyed by the store's `error_code` (§SMM-8, "per-code copy"). */
export function connectRefusalWords(code: string | null, family: string): string {
  switch (code) {
    case 'oauth_state_invalid':
      return 'That attempt to connect expired or was already used. Start again from Accounts.';
    case 'oauth_exchange_failed':
      return `${family} didn’t finish connecting. Please try again.`;
    case 'insufficient_scope':
      return `Givmo needs every permission it asked for on ${family}. Try again and keep them all turned on.`;
    case 'no_eligible_account':
      return family === 'LinkedIn'
        ? 'We didn’t find a LinkedIn page you’re an admin of.'
        : 'We didn’t find a Facebook Page you manage, or an Instagram professional account linked to one.';
    case 'provider_busy':
      return `${family} is busy right now. Try again in a minute.`;
    case 'different_account':
      return 'That’s a different account from the one you were reconnecting. Sign in with that account and try again.';
    case 'account_already_connected':
      return 'That account is already connected.';
    case 'redirect_uri_not_allowed':
      return 'Connecting accounts isn’t set up for this console yet.';
    default:
      return 'Something went wrong while connecting. Please try again.';
  }
}

/** The platform's own `error` on the return: the person (or the platform) didn't give access. */
export function providerDeclinedWords(family: string): string {
  return `Access wasn’t given on ${family}.`;
}
